import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowRight, ShieldCheck, GitBranch, RefreshCw } from "lucide-react";

const empty = { project_code: "", title: "", summary: "", locked_decisions: "", next_actions: "",
  source_url: "", execution_type: "HANDOFF" };

function errorMessage(e) {
  const detail = e.response?.data?.detail;
  return typeof detail === "string" ? detail : "Operasi belum berhasil. Periksa data atau koneksi.";
}

export default function Continuity() {
  const [searchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(searchParams.get("project") || "");
  const [form, setForm] = useState(empty);
  const [packets, setPackets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState("");
  const [evidence, setEvidence] = useState({});
  const [taskStates, setTaskStates] = useState({});
  const [loadError, setLoadError] = useState(false);

  const load = async (code = selected) => {
    setLoading(true);
    try {
      const rows = await api.contextPackets(code || undefined);
      setPackets(rows);
      const linked = rows.filter((row) => row.task_id);
      const states = await Promise.all(linked.map(async (row) => {
        try { return [row.id, await api.task(row.task_id)]; } catch { return [row.id, null]; }
      }));
      setTaskStates(Object.fromEntries(states));
      setLoadError(false);
    } catch {
      setPackets([]);
      setLoadError(true);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    api.projects().then((items) => setProjects(Array.isArray(items) ? items : [])).catch(() => {});
  }, []);
  useEffect(() => { load(selected); }, [selected]);

  const create = async (event) => {
    event.preventDefault();
    setWorking("create");
    try {
      const body = {
        ...form, project_code: selected,
        locked_decisions: form.locked_decisions.split("\n").map((x) => x.trim()).filter(Boolean),
        next_actions: form.next_actions.split("\n").map((x) => x.trim()).filter(Boolean),
      };
      await api.createContextPacket(body);
      setForm(empty);
      toast.success("Paket konteks dibuat sebagai draft. Review sebelum approve.");
      await load();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setWorking(""); }
  };

  const perform = async (id, type) => {
    setWorking(id);
    try {
      if (type === "approve") {
        await api.approveContextPacket(id);
        toast.success("Founder menyetujui paket konteks. Ini bukan approval deploy.");
      } else if (type === "task") {
        const task = await api.contextPacketToTask(id);
        toast.success(`Task ${task.id} tercatat; handoff belum berarti eksekusi.`);
      } else if (type === "run") {
        const task = packets.find((x) => x.id === id);
        if (!task?.task_id) throw Error("No task");
        const result = await api.executeGithubReadTask(task.task_id);
        setEvidence((previous) => ({ ...previous, [id]: result }));
        toast.success(result.evidence?.verified ? "GitHub terbaca; bukti menunggu review." : "Pemeriksaan diblokir; lihat buktinya.");
      }
      await load();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setWorking(""); }
  };

  return (
    <div className="space-y-6" data-testid="founder-continuity">
      <header>
        <p className="mono-label">FOUNDER CONTINUITY · PROJECT CONTEXT</p>
        <h1 className="font-display text-3xl font-extrabold text-white mt-1">Lanjutkan dari ChatGPT</h1>
        <p className="text-sm text-[#94A3B8] mt-2 max-w-3xl">
          Bawa ringkasan pembahasan yang sudah lo setujui dari ChatGPT ke MASANDI. Sistem tidak
          mengambil seluruh percakapan otomatis. Kirim paket konteks, approve, teruskan menjadi task,
          lalu pantau bukti eksekusinya.
        </p>
      </header>

      <section className="m-card p-4 space-y-3">
        <label className="block text-sm text-[#94A3B8]">Project bisnis yang dilanjutkan
          <select className="input-dark h-10 w-full mt-2 px-3 text-white" value={selected}
            onChange={(e) => setSelected(e.target.value)} data-testid="continuity-project">
            <option value="">Semua project / pilih untuk membuat handoff</option>
            {projects.map((p) => <option key={p.code} value={p.code}>{p.code} · {p.name}</option>)}
          </select>
        </label>
        <p className="text-xs text-[#FBBF24]">
          Ringkasan dari ChatGPT bersifat materi yang diberikan Founder. Keputusan di dalamnya
          tidak otomatis mengizinkan deploy, spending, migrasi, atau penggantian scope besar.
        </p>
      </section>

      {selected && <form onSubmit={create} className="m-card p-5 space-y-4" data-testid="continuity-form">
        <div className="flex items-center gap-2 text-white font-semibold"><ShieldCheck size={18} /> Paket konteks yang disetujui Founder</div>
        <label className="block text-xs text-[#94A3B8]">Judul pembahasan
          <input className="input-dark w-full h-10 px-3 mt-1" required minLength={8} maxLength={160}
            value={form.title} onChange={(e) => setForm({ ...form, title:e.target.value })}
            placeholder="Contoh: Finalisasi checkout dan dashboard TEMUJALAN" />
        </label>
        <label className="block text-xs text-[#94A3B8]">Link ChatGPT Project (opsional, referensi sumber)
          <input className="input-dark w-full h-10 px-3 mt-1" type="url" value={form.source_url}
            onChange={(e) => setForm({ ...form, source_url:e.target.value })}
            placeholder="https://chatgpt.com/g/g-p-..." />
        </label>
        <label className="block text-xs text-[#94A3B8]">Ringkasan konteks dan progres aktual (30–8000 karakter)
          <textarea className="input-dark w-full px-3 py-2 mt-1 min-h-[130px]" required minLength={30} maxLength={8000}
            value={form.summary} onChange={(e) => setForm({ ...form, summary:e.target.value })}
            placeholder="Tempel ringkasan singkat, keputusan yang sudah final, yang belum selesai, dan batasan saat ini." />
        </label>
        <div className="grid md:grid-cols-2 gap-3">
          <label className="block text-xs text-[#94A3B8]">Keputusan yang sudah dikunci (opsional, satu per baris)
            <textarea className="input-dark w-full px-3 py-2 mt-1 min-h-[100px]" value={form.locked_decisions}
              onChange={(e) => setForm({ ...form, locked_decisions:e.target.value })} />
          </label>
          <label className="block text-xs text-[#94A3B8]">Langkah berikutnya (wajib, satu per baris)
            <textarea className="input-dark w-full px-3 py-2 mt-1 min-h-[100px]" required value={form.next_actions}
              onChange={(e) => setForm({ ...form, next_actions:e.target.value })} />
          </label>
        </div>
        <label className="block text-xs text-[#94A3B8]">Mode pekerjaan
          <select className="input-dark w-full h-10 px-3 mt-1" value={form.execution_type}
            onChange={(e) => setForm({ ...form, execution_type:e.target.value })}>
            <option value="HANDOFF">Handoff eksternal — dicatat dan dipantau, bukan otomatis berjalan</option>
            <option value="GITHUB_READ_AUDIT">Eksekusi nyata: audit repository GitHub read-only</option>
          </select>
        </label>
        <button type="submit" className="btn-primary h-10 px-5 disabled:opacity-50" disabled={!!working}
          data-testid="continuity-create">{working === "create" ? "Menyimpan..." : "Simpan Draft Konteks"}</button>
      </form>}

      <section className="space-y-3" data-testid="continuity-packets">
        <div className="flex items-center justify-between">
          <h2 className="text-white font-semibold">Riwayat konteks & handoff</h2>
          <button onClick={() => load()} className="btn-ghost h-9 px-3 text-xs flex gap-2 items-center"><RefreshCw size={14}/> Muat ulang</button>
        </div>
        {loading ? <Loader2 className="text-[#60A5FA] animate-spin" /> :
          loadError ? <p role="alert" className="text-[#FB7185]">Data gagal dimuat. Coba lagi.</p> :
          packets.length === 0 ? <p className="m-card p-5 text-sm text-[#94A3B8]">Belum ada paket konteks untuk filter ini.</p> :
          packets.map((p) => (
            <div key={p.id} className="m-card p-4 space-y-3" data-testid={`continuity-packet-${p.id}`}>
              <div className="flex flex-wrap justify-between items-start gap-2">
                <div>
                  <p className="text-white font-semibold">{p.title}</p>
                  <p className="text-[#94A3B8] text-xs mt-1">{p.project_code} · {p.status} · {p.execution_type}</p>
                </div>
                {p.source_url && <a target="_blank" rel="noopener noreferrer" className="text-[#60A5FA] text-xs" href={p.source_url}>Sumber ChatGPT ↗</a>}
              </div>
              <p className="text-[#CBD5E1] text-sm whitespace-pre-wrap">{p.summary}</p>
              {p.locked_decisions?.length > 0 && <p className="text-xs text-[#E2E8F0]">Keputusan terkait: {p.locked_decisions.join(" · ")}</p>}
              <p className="text-xs text-[#94A3B8]">Berikutnya: {(p.next_actions || []).join(" · ")}</p>
              <p className="text-[11px] text-[#FBBF24]">Snapshot diserahkan Founder · bukan ChatGPT auto-sync · bukan approval production</p>
              <div className="flex flex-wrap gap-2">
                {p.status === "DRAFT" && <button className="btn-primary h-9 px-3 text-xs" disabled={!!working}
                  onClick={() => perform(p.id, "approve")} data-testid={`approve-context-${p.id}`}>Approve Paket</button>}
                {p.status === "APPROVED" && !p.task_id && <button className="btn-primary h-9 px-3 text-xs"
                  disabled={!!working} onClick={() => perform(p.id, "task")}>Buat Task <ArrowRight size={12} className="inline" /></button>}
                {p.task_id && <Link className="btn-ghost h-9 px-3 text-xs inline-flex items-center" to="/jobs">
                  Task {taskStates[p.id]?.status || "tercatat"} · Pantau Eksekusi
                </Link>}
                {p.task_id && p.execution_type === "GITHUB_READ_AUDIT" && taskStates[p.id]?.status === "READY" &&
                  <button className="btn-primary h-9 px-3 text-xs flex gap-2 items-center" disabled={!!working}
                    onClick={() => perform(p.id, "run")}><GitBranch size={13}/> Jalankan Audit Read-only</button>}
              </div>
              {(evidence[p.id] || taskStates[p.id]?.read_only_evidence) && <div className="border border-[#274268] p-3 rounded-lg text-xs space-y-1">
                <p className="text-white font-semibold">Hasil eksekusi: {(evidence[p.id]?.evidence || taskStates[p.id]?.read_only_evidence)?.state}</p>
                <p className="text-[#94A3B8]">Repo: {(evidence[p.id]?.evidence || taskStates[p.id]?.read_only_evidence)?.repository} · Branch: {(evidence[p.id]?.evidence || taskStates[p.id]?.read_only_evidence)?.branch || "—"}</p>
                <p className="text-[#94A3B8]">Commit: {(evidence[p.id]?.evidence || taskStates[p.id]?.read_only_evidence)?.commit_sha || "Tidak tersedia"}</p>
                <p className="text-[#FBBF24]">Bukti teknis tersedia; tugas menunggu review Founder. Tidak ada perubahan GitHub.</p>
              </div>}
            </div>
          ))}
      </section>
    </div>
  );
}
